import { createApi } from '@reduxjs/toolkit/query/react';
import { capitalizeString, sortAsc } from '@strata/helper-functions';

import { USER_SUPPORT_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { FMH_UPN, formatNameMaintainCase, mapConversation } from 'services/userSupportHelpers';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export const TagTypes = {
    ONBOARDING_ACTIVITY: 'ONBOARDING_ACTIVITY',
    CONVERSATION: 'CONVERSATION',
    MESSAGES: 'MESSAGES',
    CONVERSATION_FACTS: 'CONVERSATION_FACTS',
    REFERRAL_CONVERSATIONS: 'REFERRAL_CONVERSATIONS',
    PAGED_CONVERSATIONS: 'PAGED_CONVERSATIONS',
    UNREAD_COUNTS: 'UNREAD_COUNTS',
};

const checkLength = (array = []) => (array.length > 0 ? array : undefined);

/**
 * Reduces the grid's active filters to the body the conversations endpoint expects, dropping
 * empty selections. The grid reports "no filters" as both undefined and an object of empty
 * arrays, so normalising here keeps equivalent filter states on a single cache entry.
 */
const toConversationFilters = (activeFilters) => ({
    conversation_status: checkLength(activeFilters?.conversation_status),
    conversation_type: checkLength(activeFilters?.conversation_type),
    supplier_npi: checkLength(activeFilters?.supplier_npi),
    segment_id: checkLength(activeFilters?.segment_id),
    benefit: checkLength(activeFilters?.benefit),
    is_unread: checkLength(activeFilters?.is_unread),
    is_last_message_low_operational_value: checkLength(
        activeFilters?.is_last_message_low_operational_value,
    ),
    created_by_upn: checkLength(activeFilters?.created_by_upn),
    assigned_upn: checkLength(activeFilters?.assigned_upn),
    short_title: checkLength(activeFilters?.short_title),
    last_updated_by_fmh: checkLength(activeFilters?.last_updated_by_fmh),
});

/**
 * The cache key for a page of conversations, derived from the request that actually goes out
 * rather than the raw args. The grid emits several spellings of "no filters" and "no search"
 * while mounting, and they must all land on one cache entry or the page refetches itself.
 * Anything added to the endpoint's `query` must be added here too, or a change to it will
 * reuse the previous cache entry instead of refetching.
 */
export const getPagedConversationsCacheKey = ({ page, search, sort, activeFilters }) =>
    JSON.stringify({
        page,
        // A cleared search box reports '', which is the same query as no search
        search: search === '' ? undefined : search,
        sort,
        filters: toConversationFilters(activeFilters),
    });

export const userSupportApi = createApi({
    baseQuery: getBaseQuery(USER_SUPPORT_API_URL),
    tagTypes: Object.values(TagTypes),
    reducerPath: 'userSupportApi',
    refetchOnMountOrArgChange: true,
    endpoints: (build) => ({
        getConversationFilterCategories: build.query({
            query: () => 'admin/conversations/filter_options',
            transformResponse: async (response) => {
                const filterOptions = {
                    ...response,
                    conversation_status: response.conversation_status.map((item) => ({
                        text: capitalizeString(item.value),
                        value: item.value,
                    })),
                    conversation_type: response.conversation_type.map((item) => ({
                        text: capitalizeString(item.value),
                        value: item.value,
                    })),
                    supplier_npi: response.supplier_npi.filter((item) => item.value !== null),
                    is_unread: response.is_unread.map((item) => ({
                        text: item.value ? 'Unread' : 'Read',
                        value: item.value,
                    })),
                    is_last_message_low_operational_value: [
                        {
                            text: capitalizeString('Include Low Value'),
                            value: 'include_also_low_value',
                        },
                    ],
                    created_by_upn: response.created_by_upn.map(({ value, details }) => ({
                        text: formatNameMaintainCase(
                            details.created_by_first_name,
                            details.created_by_last_name,
                        ),
                        value,
                    })),
                    assigned_upn: response.assigned_upn.map(({ value, details }) => ({
                        text:
                            formatNameMaintainCase(
                                details.assigned_first_name,
                                details.assigned_last_name,
                            ) ?? 'Unassigned',
                        value,
                    })),
                    last_updated_by_fmh: response.last_updated_by_fmh.map((item) => ({
                        text: item.value ? 'FMH' : 'Pharmacy',
                        value: item.value,
                    })),
                };

                return [
                    { key: 'short_title', title: 'Conversation Topic' },
                    { key: 'last_updated_by_fmh', title: 'Last Updated By' },
                    { key: 'supplier_npi', title: 'Pharmacy' },
                    { key: 'assigned_upn', title: 'Assigned To' },
                    // Only returns one option right now, may include others in future.
                    // {
                    //     key: 'conversation_type',
                    //     title: 'Conversation Type',
                    // },
                    { key: 'conversation_status', title: 'Conversation Status' },
                    {
                        key: 'is_last_message_low_operational_value',
                        title: 'Low Value Messages',
                    },
                    { key: 'segment_id', title: 'Segment ID' },
                    { key: 'benefit', title: 'Benefit' },
                    { key: 'is_unread', title: 'Read' },
                    { key: 'created_by_upn', title: 'Created By' },
                ].map((category) => {
                    let values = filterOptions[category.key]?.sort((a, b) =>
                        sortAsc(a.text ?? a.value, b.text ?? b.value),
                    );
                    if (category.key === 'created_by_upn') {
                        values = [
                            {
                                text: 'Created by Me',
                                value: 'created_by_me',
                            },
                            {
                                text: 'FMH Support',
                                value: FMH_UPN,
                            },
                            ...values,
                        ];
                    } else if (category.key === 'assigned_upn') {
                        values = [
                            {
                                text: 'Assigned to Me',
                                value: 'assigned_to_me',
                            },
                            {
                                text: 'Unassigned',
                                value: 'unassigned',
                            },
                            ...values,
                        ];
                    }

                    return {
                        ...category,
                        customValues: values,
                    };
                });
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving conversation filter options.',
            ),
        }),
        getPagedConversations: build.query({
            query: ({ page, search, sort, activeFilters }) => ({
                url: 'admin/conversations/list',
                method: 'POST',
                params: {
                    ...(search ? { search_term: search } : {}),
                    page,
                },
                body: {
                    additionalFilters: toConversationFilters(activeFilters),
                    sortProperties: sort,
                },
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) =>
                `${endpointName}(${getPagedConversationsCacheKey(queryArgs)})`,
            providesTags: (results) => [
                TagTypes.PAGED_CONVERSATIONS,
                ...(results?.conversations ?? []).map(({ conversationId }) => ({
                    type: TagTypes.CONVERSATION,
                    id: conversationId,
                })),
            ],
            transformResponse: (response) => ({
                count: response?.count,
                conversations: response?.conversations?.map(mapConversation),
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving conversations.',
            ),
        }),
        /** Gets list of support users */
        getUsers: build.query({
            query: () => 'admin/users',
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving User Support users.',
            ),
        }),
        getOnboardingActivity: build.query({
            query: ({ referralId }) => `admin/lifecycle_events/referral/${referralId}`,
            providesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.ONBOARDING_ACTIVITY, id: referralId },
            ],
            transformResponse: (response, _meta, { referralId }) =>
                response.map((event) => ({
                    referralId,
                    eventId: event.event_id,
                    eventStatus: event.event_status,
                    metadata: event.metadata,
                    eventSource: event.event_source,
                    rawStatus: event.raw_status,
                    eventTimestamp: event.event_timestamp,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving fill activity for referral.',
            ),
        }),
        postOnboardingActivity: build.mutation({
            query: ({ referralId, body }) => ({
                url: `admin/lifecycle_events/referral/${referralId}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.ONBOARDING_ACTIVITY, id: referralId },
            ],
        }),
        deleteOnboardingStatus: build.mutation({
            query: ({ eventId }) => ({
                url: `admin/lifecycle_events/event/${eventId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.ONBOARDING_ACTIVITY, id: referralId },
            ],
        }),
        postFillActivityRequest: build.mutation({
            query: ({ conversationId }) => ({
                url: `admin/messages/${conversationId}/fill_activity_request`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred submitting fill activity request for referral.',
            ),
        }),
    }),
});

export const {
    useGetConversationFilterCategoriesQuery,
    useGetPagedConversationsQuery,
    useGetUsersQuery,
    useGetOnboardingActivityQuery,
    usePostOnboardingActivityMutation,
    useDeleteOnboardingStatusMutation,
    usePostFillActivityRequestMutation,
} = userSupportApi;
